import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { format } from 'date-fns';
import { html, nothing, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { type Observable, of } from 'rxjs';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { Role } from '../../../permissions/roles.js';
import type { Person } from '../../../users/entities/person.js';
import { ContractAPI } from '../../api/ContractAPI.js';
import type { ContractSupplementApplyHistory } from '../../entities/AppliedContract.js';
import { Contract } from '../../entities/Contract.js';
import type { ContractMetaInformation } from '../../entities/ContractMetaInformation.js';
import {
  ContractMetaInformationComment,
  ContractMetaInformationCommentType,
} from '../../entities/ContractMetaInformation.js';
import { MAVStatus } from '../../enums/MAVStatus.js';
import { ChangeList } from './changes/ChangeEntry.js';

export const CONTRACT_META_COMMENTS_SELECTION = {
  mavStatus: 1,
  comments: {
    oldProcess: 1,
    userID: 1,
    text: 1,
    _id: 1,
    createdAt: 1,
    userFullName: 1,
    userRole: 1,
    type: 1,
    linkedSupplementID: 1,
    linkedSupplement: {
      _id: 1,
      justification: 1,
      changes: { path: 1, type: 1, value: 1 },
    },
    changeMade: { paths: { path: 1, changes: { path: 1, type: 1, value: 1 } } },
  },
} as const;

type MetaWithComments = Maybe<NarrowedEntity<ContractMetaInformation, typeof CONTRACT_META_COMMENTS_SELECTION>>;
type MetaComment = NonNullable<NonNullable<MetaWithComments>['comments']>[number];

/** the initial hiring process is finished once its comments are flagged as old */
export const isInitialContractProcessFinished = (
  meta: Maybe<NarrowedEntity<ContractMetaInformation, { comments: { linkedSupplementID: 1; oldProcess: 1 } }>>,
) => meta?.comments?.find(c => !c.linkedSupplementID)?.oldProcess ?? false;

/** whether a new MAV process could be started */
export const isInitialProcessMAVReady = (meta: Maybe<NarrowedEntity<ContractMetaInformation, { mavStatus: 1 }>>) => {
  if (!meta?.mavStatus) return true;
  return [MAVStatus.MAV_BESTAETIGT, MAVStatus.MAV_ABGELEHNT].includes(meta.mavStatus as MAVStatus);
};

interface CommentContext {
  contractID: Maybe<string>;
  meta: MetaWithComments;
  user: Maybe<Person>;
  justification: Maybe<string>;
  userComment: string;
  setUserComment: (value: string) => void;
  onContractRemoved?: () => void;
}

const getUserRole = (ctx: CommentContext) =>
  ctx.user?.roles.find(r => r.name === Role.SCHULLEITER)
    ? Role.SCHULLEITER
    : ctx.user?.roles.find(r => r.name === Role.MAV)
      ? Role.MAV
      : Role.PSB;

const CommentUserMeta = (comment: MetaComment) => html`
  <span class="text-sm text-base-content/70">
    ${comment.userFullName}, ${format(comment.createdAt, 'dd.MM.yyyy HH:mm')}
  </span>
`;

const CommentCard = (options: {
  tone: 'success' | 'error' | 'warning' | 'neutral';
  title: TemplateResult | string;
  comment: MetaComment;
  content: TemplateResult | typeof nothing;
  wide?: boolean;
}) => {
  const toneClass = {
    success: 'bg-success/10 border border-success/30',
    error: 'bg-error/10 border border-error/30',
    warning: 'bg-warning/10 border border-warning/30',
    neutral: 'bg-base-200 border border-base-300',
  }[options.tone];

  return html`
    <div class="rounded-xl p-4 flex flex-col gap-3 ${toneClass} ${options.wide ? 'min-w-0 lg:min-w-[800px]' : ''}">
      <div class="flex flex-row items-center justify-between gap-4 border-b border-base-300 pb-2">
        <h3 class="font-semibold">${options.title}</h3>
        ${CommentUserMeta(options.comment)}
      </div>
      ${options.content}
    </div>
  `;
};

const handleSendComment = async (ctx: CommentContext, comment: MetaComment) => {
  if (!ctx.contractID || !ctx.meta)
    throw new Error('No contract id given. The meta process chat needs a contract to work on');

  if (!(await confirmDialog('Kommentar zur MAV senden?'))) return;

  const userComment = ctx.userComment;

  ctx.meta.comments?.push(
    new ContractMetaInformationComment({
      userID: ctx.user?._id,
      userRole: getUserRole(ctx),
      type: ContractMetaInformationCommentType.CHAT_MESSAGE,
      linkedSupplementID: comment.linkedSupplementID,
      text: userComment,
    }),
  );

  ctx.setUserComment('');

  ctx.meta.mavStatus = MAVStatus.MAV_BETEILIGEN;

  await guardDialog(async () => {
    if (!ctx.contractID || !ctx.meta) return;
    await (ctx.meta as ContractMetaInformation).save();

    await ContractAPI.notifyChange(
      ctx.contractID,
      Role.MAV,
      'auf Ihre Rückfrage wurde geantwortet: ' + userComment,
      comment.linkedSupplementID ?? undefined,
    )();
  });
};

const handleStepBack = async (ctx: CommentContext, comment: MetaComment) => {
  if (!ctx.meta) throw new Error('No contract Meta found. Should be automatically created on request');
  const meta = ctx.meta as ContractMetaInformation;

  // if the process is about a supplement we want to
  if (comment.linkedSupplementID) {
    if (
      !(await confirmDialog(
        'Wollen Sie die Änderungen wirklich zurückziehen? Der Nachtrag wird dadurch gelöscht und die Änderungen gehen verloren. Die Dokumentation der Schritte bleibt erhalten.',
        {
          highlightDecline: true,
          declineText: 'Abbrechen',
          acceptText: 'Änderungen zurückziehen',
        },
      ))
    )
      return;

    // set every comment to old process and mark the supplement as deleted
    for (const c of meta.comments ?? []) {
      if (c.linkedSupplementID === comment.linkedSupplementID) {
        // we mark it as deleted but we need the id to be able to distinguish it from other deleted supplements
        c.linkedSupplementID = c.linkedSupplementID + '_removed';
        c.oldProcess = true;
      }
    }

    // add the final comment which states that the principal has deleted the supplement
    meta.comments?.push(
      new ContractMetaInformationComment({
        userID: ctx.user?._id,
        userRole: getUserRole(ctx),
        type: ContractMetaInformationCommentType.SUPPLEMENT_DELETED,
        linkedSupplementID: comment.linkedSupplementID,
        text: 'Änderung wurde von der Schulleitung zurückgezogen.',
        oldProcess: true,
      }),
    );

    // reset mav status to MAV_BESTAETIGT as this is the normal state for contracts with supplements
    meta.mavStatus = MAVStatus.MAV_BESTAETIGT;

    await guardDialog(
      async () => {
        await meta.save();
        await comment.linkedSupplement?.remove();
      },
      {
        success: () => {
          void SgSnackbar.show('Änderung zurückgezogen!');
        },
      },
    );
  } else {
    if (
      !(await confirmDialog(
        'Wollen Sie die Neueinstellung wirklich zurückziehen? Der Vetrag und alle Informationen werden hiermit unwiederbringlich gelöscht.',
        {
          highlightDecline: true,
          declineText: 'Abbrechen',
          acceptText: 'Neueinstellung zurückziehen',
        },
      ))
    )
      return;

    await guardDialog(
      async () => {
        if (!ctx.contractID) return;
        const contract = await getByID(Contract, ctx.contractID)({ _id: 1 });
        if (!contract) return;
        await contract.remove();
        await meta.remove();
      },
      {
        success: () => {
          void SgSnackbar.show('Neueinstellung zurückgezogen!');
          ctx.onContractRemoved?.();
        },
      },
    );
  }
};

const handleMAVAccept = async (ctx: CommentContext, comment: MetaComment) => {
  if (!ctx.contractID || !ctx.meta)
    throw new Error('No contract id given. The meta process chat needs a contract to work on');
  const meta = ctx.meta as ContractMetaInformation;

  // if the mav accepts from the psb the process should close as this is the final accept. If not the mav accepts but the process needs to get trough another rotation for the psbs
  const mavShouldAcceptFromPSB = comment.userRole === Role.PSB;

  if (comment.linkedSupplementID) {
    if (
      !(await confirmDialog('Wollen Sie die Änderungen wirklich annehmen?', {
        acceptText: 'Änderungen annehmen',
        declineText: 'Abbrechen',
      }))
    )
      return;

    for (const c of meta.comments ?? []) {
      if (c.linkedSupplementID === comment.linkedSupplementID) {
        c.oldProcess = true;
      }
    }

    meta.comments?.push(
      new ContractMetaInformationComment({
        userRole: getUserRole(ctx),
        type: ContractMetaInformationCommentType.MAV_ACCEPT,
        linkedSupplementID: comment.linkedSupplementID,
        text: ctx.userComment,
        userID: ctx.user?._id,
        oldProcess: true,
      }),
    );

    meta.mavStatus = MAVStatus.MAV_BESTAETIGT;

    await guardDialog(
      async () => {
        await meta.save();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.SCHULLEITER,
          'die MAV hat der Änderung zugestimmt. Die Personalverwaltung wurde benachrichtigt.',
          comment.linkedSupplementID ?? undefined,
        )();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.PSB,
          'die MAV hat einer Änderung zugestimmt und ein Nachtrag wurde erstellt.',
          comment.linkedSupplementID ?? undefined,
        )();
      },
      {
        success: () => {
          void SgSnackbar.show('Änderungen angenommen!');
        },
      },
    );
  } else if (mavShouldAcceptFromPSB) {
    if (
      !(await confirmDialog('Wollen Sie den Änderungen der Personalabteilung wirklich annehmen?', {
        acceptText: 'Änderungen annehmen',
        declineText: 'Abbrechen',
      }))
    )
      return;

    for (const c of meta.comments ?? []) {
      if (!c.linkedSupplementID) {
        c.oldProcess = true;
      }
    }

    meta.comments?.push(
      new ContractMetaInformationComment({
        userRole: getUserRole(ctx),
        type: ContractMetaInformationCommentType.MAV_ACCEPT,
        text: ctx.userComment,
        userID: ctx.user?._id,
        oldProcess: true,
      }),
    );

    meta.mavStatus = MAVStatus.MAV_BESTAETIGT;

    await guardDialog(
      async () => {
        await meta.save();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.SCHULLEITER,
          'die MAV hat den Änderungen der Personalabteilung zugestimmt. Der Vertrag ist nun bereit zum Unterzeichnen.',
          comment.linkedSupplementID ?? undefined,
        )();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.PSB,
          'die MAV hat den Änderungen zugestimmt.',
          comment.linkedSupplementID ?? undefined,
        )();
      },
      {
        success: () => {
          void SgSnackbar.show('Neueinstellung angenommen!');
        },
      },
    );
  } else {
    if (
      !(await confirmDialog('Wollen Sie die Neueinstellung wirklich annehmen?', {
        acceptText: 'Neueinstellung annehmen',
        declineText: 'Abbrechen',
      }))
    )
      return;

    meta.comments?.push(
      new ContractMetaInformationComment({
        userRole: getUserRole(ctx),
        type: ContractMetaInformationCommentType.MAV_ACCEPT,
        text: ctx.userComment,
        userID: ctx.user?._id,
      }),
    );

    meta.mavStatus = MAVStatus.MAV_BESTAETIGT;

    await guardDialog(
      async () => {
        await meta.save();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.SCHULLEITER,
          'die MAV hat der Neueinstellung zugestimmt. Die Personalverwaltung wurde benachrichtigt.',
          comment.linkedSupplementID ?? undefined,
        )();
        await ContractAPI.notifyChange(
          ctx.contractID ?? '',
          Role.PSB,
          'die MAV hat einer Neueinstellung zugestimmt.',
          comment.linkedSupplementID ?? undefined,
        )();
      },
      {
        success: () => {
          void SgSnackbar.show('Neueinstellung bestätigt!');
        },
      },
    );
  }

  ctx.setUserComment('');

  goTo('/personen');
};

const handleQuestionMAV = async (ctx: CommentContext, comment: MetaComment) => {
  if (!ctx.meta) return;
  const meta = ctx.meta as ContractMetaInformation;
  if (!ctx.userComment) {
    await alertDialog('Bitte geben Sie einen Kommentar ein.');
    return;
  }
  if (
    !(await confirmDialog('Soll die Rückfrage wirklich gestellt werden?', {
      acceptText: 'Rückfrage stellen',
      declineText: 'Abbrechen',
    }))
  )
    return;

  const userComment = ctx.userComment;

  meta.comments?.push(
    new ContractMetaInformationComment({
      userRole: getUserRole(ctx),
      type:
        comment.userRole === Role.PSB
          ? ContractMetaInformationCommentType.MAV_QUESTION_PSB
          : ContractMetaInformationCommentType.MAV_QUESTION_PRINCIPAL,
      linkedSupplementID: comment.linkedSupplementID,
      text: userComment,
      userID: ctx.user?._id,
    }),
  );

  meta.mavStatus = MAVStatus.MAV_RUECKFRAGE;

  await guardDialog(async () => {
    await meta.save();
    if (!comment.userRole) return;
    await ContractAPI.notifyChange(
      ctx.contractID ?? '',
      comment.userRole,
      'die MAV hat eine Rückfrage gestellt: ' + userComment,
      comment.linkedSupplementID ?? undefined,
    )();
  });

  ctx.setUserComment('');
};

const handleRejectMAV = async (ctx: CommentContext, comment: MetaComment) => {
  if (!ctx.meta) return;
  const meta = ctx.meta as ContractMetaInformation;
  if (!ctx.userComment) {
    await alertDialog('Bitte geben Sie einen Kommentar ein.');
    return;
  }
  if (
    !(await confirmDialog('Sind Sie sicher, dass Sie ablehnen wollen?', {
      highlightDecline: true,
      acceptText: 'Ablehnen',
      declineText: 'Abbrechen',
    }))
  )
    return;

  const userComment = ctx.userComment;

  meta.comments?.push(
    new ContractMetaInformationComment({
      userRole: getUserRole(ctx),
      type: ContractMetaInformationCommentType.MAV_REJECTION,
      linkedSupplementID: comment.linkedSupplementID,
      text: userComment,
      userID: ctx.user?._id,
    }),
  );

  meta.mavStatus = MAVStatus.MAV_ABGELEHNT;

  await guardDialog(async () => {
    await meta.save();
    if (!comment.userRole) return;
    await ContractAPI.notifyChange(
      ctx.contractID ?? '',
      comment.userRole,
      'die MAV hat der Änderung nicht zugestimmt.' + (userComment ? ' Kommentar: ' + userComment : ''),
      comment.linkedSupplementID ?? undefined,
    )();
  });

  ctx.setUserComment('');

  goTo('/personen');
};

const CommentField = (ctx: CommentContext) =>
  TextareaInput({
    value: ctx.userComment,
    placeholder: 'Kommentar',
    onPicked: (value: string | null) => ctx.setUserComment(value ?? ''),
  });

const MAVWaitCard = () => html`
  <div class="rounded-xl p-4 flex flex-row items-center gap-3 bg-base-200">
    <h3 class="font-semibold">Wartet auf MAV</h3>
    <span class="icon-[material-symbols--hourglass-empty] text-3xl"></span>
  </div>
`;

const MavCommentCard = (ctx: CommentContext, comment: MetaComment, last = false) => {
  if (!last || ctx.meta?.mavStatus !== MAVStatus.MAV_BETEILIGEN) return nothing;

  return ctx.user?.roles.find(r => r.name === Role.MAV)
    ? html`
        <div class="card p-4 gap-3">
          <h3 class="font-semibold border-b border-base-300 pb-2">MAV Beteiligung</h3>

          ${CommentField(ctx)}

          <div class="flex flex-row flex-wrap gap-2 rounded-lg bg-base-100 p-1">
            <button class="btn btn-error flex-1" @click=${() => handleRejectMAV(ctx, comment)}>Ablehnen</button>
            <button class="btn btn-warning flex-1" @click=${() => handleQuestionMAV(ctx, comment)}>Rückfragen</button>
            <button class="btn btn-success flex-1 text-white" @click=${() => handleMAVAccept(ctx, comment)}>
              Annehmen
            </button>
          </div>
        </div>
      `
    : MAVWaitCard();
};

const renderCommentCard = (ctx: CommentContext, comment: MetaComment, last = false): unknown => {
  switch (comment.type) {
    case ContractMetaInformationCommentType.NEW_SUPPLEMENT:
      return html`
        ${CommentCard({
          tone: 'success',
          wide: true,
          title: `Neuer Nachtrag vom ${format(comment.createdAt, 'dd.MM.yyyy')}`,
          comment,
          content: html`
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-base-content/70">Begründung</span>
              <p>${comment.linkedSupplement?.justification ?? 'Keine Begründung angegeben.'}</p>
            </div>

            ${
              comment.text
                ? html`
                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-semibold text-base-content/70">Zusätzliche Informationen</span>
                      <p>${comment.text}</p>
                    </div>
                  `
                : nothing
            }

            <hr class="border-base-300" />

            <p class="text-sm text-base-content/70">
              Es wurden Änderungen für den Vertrag vorgeschlagen. Im Folgenden sind diese Änderungen aufgeführt.
            </p>
            ${ChangeList({
              changes: comment.linkedSupplement?.changes,
              history: comment.changeMade as ContractSupplementApplyHistory,
              emptyState: html`<span class="font-semibold"> Änderungen wurden zurückgezogen. </span>`,
            })}
          `,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    case ContractMetaInformationCommentType.MAV_REJECTION:
      return html`
        ${CommentCard({
          tone: 'error',
          title: 'Änderung wurde von der MAV abgelehnt.',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${
          ctx.user?.roles.find(r => r.name === Role.SCHULLEITER) && last
            ? html`
                <div class="rounded-xl p-4 flex flex-col gap-3 bg-base-200">
                  <h3 class="font-semibold border-b border-base-300 pb-2">Entwurf ändern oder zurückziehen</h3>
                  <p>
                    Die MAV hat den Entwurf abgelehnt. Sie können den Entwurf nun zurückziehen und damit entgültig
                    schließen oder den Entwurf anpassen und erneut eröffnen.
                  </p>
                  <button class="btn btn-error" @click=${() => handleStepBack(ctx, comment)}>
                    ${comment.linkedSupplementID ? 'Änderung' : 'Neueinstellung'} zurückziehen
                  </button>
                </div>
              `
            : nothing
        }
      `;
    case ContractMetaInformationCommentType.SUPPLEMENT_REOPENED:
      return html`
        ${CommentCard({
          tone: 'success',
          title: 'Entwurf wurde neu eröffnet.',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    case ContractMetaInformationCommentType.MAV_QUESTION_PRINCIPAL:
    case ContractMetaInformationCommentType.MAV_QUESTION_PSB: {
      // the question can talk to different other persons depending on what the question was issued
      const roleForQuestion =
        comment.type === ContractMetaInformationCommentType.MAV_QUESTION_PSB ? Role.PSB : Role.SCHULLEITER;
      return html`
        ${CommentCard({
          tone: 'neutral',
          title: 'MAV stellt Rückfrage',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${
          ctx.user?.roles.find(r => r.name === roleForQuestion) && last
            ? html`
                <div class="rounded-xl p-4 flex flex-col gap-3 bg-base-200">
                  <h3 class="font-semibold border-b border-base-300 pb-2">MAV Antworten</h3>
                  <p>
                    Die MAV hat eine Rückfrage zum
                    ${comment.linkedSupplementID ? 'Nachtragsentwurf' : 'Arbeitsvertragsentwurf'} gestellt. Sie können
                    den Entwurf jetzt entweder zurückziehen, einen Kommentar senden oder den Entwurf nochmal ändern.
                  </p>
                  ${CommentField(ctx)}
                  <div class="flex flex-row flex-wrap gap-2 rounded-lg bg-base-100 p-1">
                    <button class="btn btn-error flex-1" @click=${() => handleStepBack(ctx, comment)}>
                      ${comment.linkedSupplementID ? 'Nachtragsentwurf' : 'Arbeitsvertragsentwurf'} zurückziehen
                    </button>
                    <button class="btn btn-warning flex-1" @click=${() => handleSendComment(ctx, comment)}>
                      Kommentar senden
                    </button>
                  </div>
                </div>
              `
            : nothing
        }
      `;
    }
    case ContractMetaInformationCommentType.SUPPLEMENT_DELETED:
      return CommentCard({
        tone: 'error',
        title: 'Änderung wurde von der Schulleitung zurückgezogen',
        comment,
        content: html`
          <p>Die vorgeschlagene Änderung wurde am ${format(comment.createdAt, 'dd.MM.yyyy')} zurückgezogen.</p>
        `,
      });
    case ContractMetaInformationCommentType.CHAT_MESSAGE:
      return html`
        ${CommentCard({
          tone: 'neutral',
          title: 'Antwort',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    case ContractMetaInformationCommentType.MAV_ACCEPT:
      return CommentCard({
        tone: 'success',
        title: comment.linkedSupplementID
          ? 'Die MAV hat die Änderungen angenommen.'
          : 'Die MAV hat der Neueinstellung zugestimmt.',
        comment,
        content: html`
          <p>
            ${
              comment.text
                ? comment.text
                : comment.linkedSupplementID
                  ? 'Die MAV hat den vorgeschlagenen Änderungen zugestimmt. Der Nachtrag ist nun bereit für die Unterzeichnung.'
                  : 'Die Personalabteilung übernimmt nun die Fertigstellung des Vertrages.'
            }
          </p>
        `,
      });
    case ContractMetaInformationCommentType.PSB_MANUAL:
      return html`
        ${CommentCard({
          tone: 'success',
          title: 'Die Personalabteilung hat Änderungen vorgenommen oder einen neuen Vertrag erstellt.',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    case ContractMetaInformationCommentType.MAV_SKIPPED:
      return html`
        ${CommentCard({
          tone: 'warning',
          title: 'Der MAV-Prozess wurde übersprungen.',
          comment,
          content: html`<p>${comment.text ?? 'Kein Kommentar'}</p>`,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    case ContractMetaInformationCommentType.CONTRACT_CREATED:
      return html`
        ${CommentCard({
          tone: 'success',
          wide: true,
          title: 'Ein neuer Vertrag wurde angelegt.',
          comment,
          content: html`
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-base-content/70">Begründung</span>
              <p>${ctx.justification ?? 'Keine Begründung angegeben.'}</p>
            </div>

            ${
              comment.text
                ? html`
                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-semibold text-base-content/70">Zusätzliche Informationen</span>
                      <p>${comment.text}</p>
                    </div>
                  `
                : nothing
            }
          `,
        })}
        ${MavCommentCard(ctx, comment, last)}
      `;
    default:
      return html`<p>${JSON.stringify((comment as ContractMetaInformationComment).toJSON?.() ?? comment)}</p>`;
  }
};

interface ContractProcessCommentsProps {
  contractID: Maybe<string>;
  onContractRemoved?: () => void;
}

/**
 * Shows the MAV process history of a contract and lets the involved roles react (accept, reject, question, comment).
 */
export const ContractProcessComments = stateful((props: ContractProcessCommentsProps) => {
  const user = useObservable(() => currentUser<Person>(), []);
  const [userComment, setUserComment] = useState('');

  const { value: contract } = useQuery<Maybe<NarrowedEntity<Contract, { justification: 1; _id: 1 }>>>(
    () => (props.contractID ? getByID(Contract, props.contractID)({ justification: 1, _id: 1 }) : of(null)),
    [props.contractID],
  );

  const { value: meta, loading: metaLoading } = useQuery<MetaWithComments>(
    () =>
      props.contractID
        ? (ContractAPI.subscribeMeta(props.contractID)(
            CONTRACT_META_COMMENTS_SELECTION,
          ) as Observable<MetaWithComments>)
        : of(null),
    [props.contractID],
  );

  if (!props.contractID) return nothing;
  if (metaLoading) return html`<div class="card card-body"><div class="skeleton h-16 w-full"></div></div>`;

  const ctx: CommentContext = {
    contractID: props.contractID,
    meta,
    user,
    justification: contract?.justification,
    userComment,
    setUserComment,
    onContractRemoved: props.onContractRemoved,
  };

  // build a map after the supplementid if no supplement id is given it is on the base contract and also its own category
  const commentMap = new Map<string, MetaComment[]>();
  meta?.comments?.forEach(comment => {
    const key = comment.linkedSupplementID ?? 'contract';
    commentMap.set(key, [...(commentMap.get(key) ?? []), comment]);
  });

  // arrays, not map iterators: `repeat` is re-run on every commit and a spent iterator renders nothing
  const groups = Array.from(commentMap.entries());
  const old = groups.filter(d => d[1].at(0)?.oldProcess);
  const newProcess = groups.filter(d => !d[1].at(0)?.oldProcess).flatMap(r => r[1]);

  return html`
    <div class="flex flex-col gap-2">
      ${repeat(
        [...old],
        ([key]) => key,
        ([, value]) => html`
          <div
            class="card p-4 cursor-pointer"
            @click=${() => {
              void showDialog({
                content: () => html`
                  <div class="flex flex-col gap-2 p-4">
                    ${repeat(
                      value,
                      comment => comment._id,
                      comment => renderCommentCard(ctx, comment),
                    )}
                  </div>
                `,
                modalBoxClasses: 'max-w-4xl',
              }).catch(() => undefined);
            }}
          >
            <h3 class="font-semibold">
              ${
                value.at(0)?.linkedSupplementID
                  ? `Vetragsänderung vom ${format(value.at(0)!.createdAt, 'dd.MM.yyyy')}`
                  : !value.at(0)?.userID
                    ? `Automatische MAV Migration vom ${format(value.at(0)!.createdAt, 'dd.MM.yyyy')}`
                    : `Neueinstellung vom ${format(value.at(0)!.createdAt, 'dd.MM.yyyy')}`
              }
            </h3>
          </div>
        `,
      )}
      ${repeat(
        [...newProcess],
        comment => comment._id,
        (comment, index) => renderCommentCard(ctx, comment, index === newProcess.length - 1),
      )}
    </div>
  `;
});
